/**
 * Display mappings and utilities
 * These mappings are used to display user-friendly labels and icons for various enums
 * including retrofits and energy sources
 */

import type { EnergySourceIconKey } from '@/assets/icons/energy-sources'
import type { HousePartIconKey } from '@/assets/icons/house-parts'
import type { EnergySource, RetrofitName } from '@/shared/types'

// Mapping from Retrofit enum values to icon keys
export const retrofitToIcon: Record<RetrofitName, HousePartIconKey> = {
  roof: 'roofInsulation',
  airWaterHeatPump: 'heatpump',
  airAirHeatPump: 'airAirHeatpump',
  groundSourceHeatPump: 'groundSourceHeatPump',
  pelletHeating: 'pelletHeating',
  windows: 'window',
  roofWindows: 'roofWindow',
  basementCeiling: 'basementCeiling',
  door: 'door',
  exteriorWallETICS: 'walls',
  floorPlate: 'floorPlate',
  topFloorCeiling: 'upperFloor',
  basementExteriorWall: 'basementWall',
  decentralVentilation: 'ventilation',
  blowInInsulation: 'blowInInsulation',
  districtHeating: 'districtHeating',
}

// Display names for retrofit types (German labels)
export const retrofitDisplayNames: Record<RetrofitName, string> = {
  roof: 'Dach dämmen',
  airWaterHeatPump: 'Wärmepumpe',
  airAirHeatPump: 'Luft-Luft Wärmepumpe',
  groundSourceHeatPump: 'Sole Wärmepumpe',
  pelletHeating: 'Pelletheizung',
  windows: 'Fenster tauschen',
  roofWindows: 'Dachfenster austauschen',
  basementCeiling: 'Kellerdecke dämmen',
  door: 'Tür austauschen',
  exteriorWallETICS: 'Außenwand dämmen: WDVS',
  floorPlate: 'Bodenplatte dämmen',
  topFloorCeiling: 'Geschossdecke dämmen',
  basementExteriorWall: 'Kellerwand dämmen',
  decentralVentilation: 'Dezentrale Lüftung',
  blowInInsulation: 'Außenwand dämmen: Einblasdämmung',
  districtHeating: 'Fernwärme',
}

/**
 * Get German display name for retrofit type
 */
export const getRetrofitDisplayName = (retrofit: RetrofitName): string => {
  return retrofitDisplayNames[retrofit] || retrofit
}

// Mapping from EnergySource enum values to icon keys
export const energySourceToIcon: Record<EnergySource, EnergySourceIconKey> = {
  heatingOil: 'oil',
  bioOil: 'oil',
  gas: 'gas',
  bioGas: 'gas',
  liquefiedGas: 'gas',
  firewood: 'biomass',
  pellets: 'biomass',
  biomass: 'biomass',
  electricity: 'electric',
  districtHeatHeatPlant: 'district',
  districtHeatHeatPlantFossil: 'district',
  districtHeatHeatPlantRenewable: 'district',
  districtHeatCHPFossil: 'district',
  districtHeatCHPRenewable: 'district',
  mineralCoal: 'other',
  brownCoal: 'other',
  blackCoal: 'other',
  solar: 'electric',
  coke: 'other',
}

/**
 * Get icon key for energy source type
 */
export const getEnergySourceIcon = (energySource: EnergySource): EnergySourceIconKey => {
  return energySourceToIcon[energySource]
}
